import { ClerkWrapper } from '@components/ClerkWrapper';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { firmApi } from '@api/firm/firm.api';
import { networkApi } from '@api/network/network.api';
import { mortgageClubApi } from '@api/mortgage-club/mortgageClub.api';
import { userApi } from '@api/user/userApi';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { NewSignupPageSectionEnum } from '@pages/SignupPage/enums';

import { useQueryClient } from '@tanstack/react-query';
import { BROKER_ONBOARDING_QUERY_KEY } from '@queries/broker/useBrokerOnboarding';
import { AuthorisationType } from '@pages/SignupPage/dtos/firm.dto';
import { SignupOrganisationFormFields } from './signupOrganisationForm.types';
import { getEmptySignupForm, toCreateNetworkAddress, toCreateClubAddress, applyFcaLookupResult } from './mappers';
import { runFirmFcaLookup, runNetworkFcaLookup, runClubFcaLookup } from './fcaLookup';
import { FirmForm } from './forms/FirmForm';
import { NetworkForm } from './forms/NetworkForm';
import { ClubForm } from './forms/ClubForm';
import { FcaResultKind } from './fcaResultKind';
import { brokerOrgFormRoot, firmField, formPath, networkField, orgField } from './formFieldPaths';

function buildHasHppPayload(authorisationType: AuthorisationType | null, has: boolean | null): true | false | null {
  if (authorisationType === AuthorisationType.AUTHORISED_REPRESENTATIVE) {
    return false;
  }
  if (has === true) {
    return true;
  }
  if (has === false) {
    return false;
  }
  return null;
}

export function CreateOrganisation() {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [firmFcaLoading, setFirmFcaLoading] = useState(false);
  const [networkFcaLoading, setNetworkFcaLoading] = useState(false);
  const [clubFcaLoading, setClubFcaLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firmFcaAttempted, setFirmFcaAttempted] = useState(false);
  const [networkFcaAttempted, setNetworkFcaAttempted] = useState(false);
  const [clubFcaAttempted, setClubFcaAttempted] = useState(false);

  const form = useForm<SignupOrganisationFormFields>({
    defaultValues: getEmptySignupForm(),
    shouldUnregister: false,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, setValue, getValues, trigger } = form;
  const authorisationType = useWatch({
    name: formPath(brokerOrgFormRoot.firm, firmField.authorisationType),
    control: form.control,
  });
  const isPartOfClubVal = useWatch({
    name: formPath(brokerOrgFormRoot.firm, firmField.isPartOfClub),
    control: form.control,
  });
  const isPartOfMortgageClubNet = useWatch({
    name: formPath(brokerOrgFormRoot.network, networkField.isPartOfMortgageClub),
    control: form.control,
  });
  const firmBackendId = useWatch({ name: formPath(brokerOrgFormRoot.firm, orgField.backendId), control: form.control });
  const networkBackendId = useWatch({
    name: formPath(brokerOrgFormRoot.network, orgField.backendId),
    control: form.control,
  });
  const clubBackendId = useWatch({
    name: formPath(brokerOrgFormRoot.mortgageClub, orgField.backendId),
    control: form.control,
  });

  const firmFcaValue = useWatch({
    name: formPath(brokerOrgFormRoot.firm, orgField.financialConductAuthorityNumber),
    control: form.control,
  });
  const networkFcaValue = useWatch({
    name: formPath(brokerOrgFormRoot.network, orgField.financialConductAuthorityNumber),
    control: form.control,
  });
  const clubFcaValue = useWatch({
    name: formPath(brokerOrgFormRoot.mortgageClub, orgField.financialConductAuthorityNumber),
    control: form.control,
  });

  useEffect(() => {
    setFirmFcaAttempted(false);
  }, [firmFcaValue]);

  useEffect(() => {
    setNetworkFcaAttempted(false);
  }, [networkFcaValue]);

  useEffect(() => {
    setClubFcaAttempted(false);
  }, [clubFcaValue]);

  const isAr = authorisationType === AuthorisationType.AUTHORISED_REPRESENTATIVE;
  const isDa = authorisationType === AuthorisationType.DIRECTLY_AUTHORISED;
  const showNetwork = isAr && !firmBackendId;
  const showClub =
    !firmBackendId &&
    ((isDa && isPartOfClubVal === true) || (isAr && isPartOfMortgageClubNet === true && !networkBackendId));

  const firmFieldsAfterFca = firmFcaAttempted || Boolean(firmBackendId);
  const networkFieldsAfterFca = networkFcaAttempted || Boolean(networkBackendId);
  const clubFieldsAfterFca = clubFcaAttempted || Boolean(clubBackendId);

  useEffect(() => {
    if (authorisationType === AuthorisationType.AUTHORISED_REPRESENTATIVE) {
      setValue(formPath(brokerOrgFormRoot.firm, firmField.hasHppPermissions), false, { shouldValidate: true });
    }
  }, [authorisationType, setValue]);

  const onFirmFca = useCallback(async () => {
    const valid = await trigger(formPath(brokerOrgFormRoot.firm, orgField.financialConductAuthorityNumber));
    if (!valid) {
      return;
    }
    setFirmFcaLoading(true);
    try {
      const fca = getValues(formPath(brokerOrgFormRoot.firm, orgField.financialConductAuthorityNumber)) as string;
      const r = await runFirmFcaLookup(fca);
      applyFcaLookupResult(setValue, { section: 'firm', result: r });
      if (r.kind === FcaResultKind.NotFound) {
        enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.fcaFirmNotFound' }), { variant: 'warning' });
      }
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(intl.formatMessage({ id: 'common.error.somethingWrong' }), { variant: 'error' });
    } finally {
      setFirmFcaLoading(false);
      setTimeout(() => {
        setFirmFcaAttempted(true);
      }, 0);
    }
  }, [getValues, setValue, trigger, enqueueSnackbar, intl]);

  const onNetworkFca = useCallback(async () => {
    const valid = await trigger(formPath(brokerOrgFormRoot.network, orgField.financialConductAuthorityNumber));
    if (!valid) {
      return;
    }
    setNetworkFcaLoading(true);
    try {
      const fca = getValues(formPath(brokerOrgFormRoot.network, orgField.financialConductAuthorityNumber)) as string;
      const r = await runNetworkFcaLookup(fca);
      applyFcaLookupResult(setValue, { section: 'network', result: r });
      if (r.kind === FcaResultKind.NotFound) {
        enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.fcaNetworkNotFound' }), { variant: 'warning' });
      }
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(intl.formatMessage({ id: 'common.error.somethingWrong' }), { variant: 'error' });
    } finally {
      setNetworkFcaLoading(false);
      setTimeout(() => {
        setNetworkFcaAttempted(true);
      }, 0);
    }
  }, [getValues, setValue, trigger, enqueueSnackbar, intl]);

  const onClubFca = useCallback(async () => {
    const valid = await trigger(formPath(brokerOrgFormRoot.mortgageClub, orgField.financialConductAuthorityNumber));
    if (!valid) {
      return;
    }
    setClubFcaLoading(true);
    try {
      const fca = getValues(
        formPath(brokerOrgFormRoot.mortgageClub, orgField.financialConductAuthorityNumber),
      ) as string;
      const r = await runClubFcaLookup(fca);
      applyFcaLookupResult(setValue, { section: 'mortgageClub', result: r });
      if (r.kind === FcaResultKind.NotFound) {
        enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.fcaClubNotFound' }), { variant: 'warning' });
      }
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(intl.formatMessage({ id: 'common.error.somethingWrong' }), { variant: 'error' });
    } finally {
      setClubFcaLoading(false);
      setTimeout(() => {
        setClubFcaAttempted(true);
      }, 0);
    }
  }, [getValues, setValue, trigger, enqueueSnackbar, intl]);

  const navigateBack = () => {
    const next = new URLSearchParams(searchParams);
    next.set('section', NewSignupPageSectionEnum.POST_SIGN_UP_PAGE);
    next.delete('step');
    setSearchParams(next);
  };

  const showFirmContinueButton = !firmFcaLoading && !firmFieldsAfterFca;
  const showNetworkContinueButton = showNetwork && !networkFcaLoading && !networkFieldsAfterFca;
  const showClubContinueButton = showClub && !clubFcaLoading && !clubFieldsAfterFca;
  const showConfirmButton = !showFirmContinueButton && !showNetworkContinueButton && !showClubContinueButton;

  const onSubmit = async (formValues: SignupOrganisationFormFields) => {
    const err = intl.formatMessage({ id: 'common.error.somethingWrong' });
    setIsSubmitting(true);
    try {
      if (formValues.firm.backendId) {
        await firmApi.linkBrokerToExistingFirm(formValues.firm.backendId);
        await queryClient.invalidateQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY] });
        await queryClient.refetchQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY], type: 'active' });

        enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.joinExistingFirmSuccess' }), { variant: 'info' });
        return;
      }

      const hasH = buildHasHppPayload(formValues.firm.authorisationType, formValues.firm.hasHppPermissions);

      const res = await userApi.createAssociatedCompany({
        name: formValues.firm.name,
        phoneNumber: formValues.firm.phoneNumber,
        email: undefined,
        principalOrDirectorName: formValues.firm.principalOrDirectorName,
        companyNumber: formValues.firm.companyNumber,
        financialConductAuthorityNumber: formValues.firm.financialConductAuthorityNumber,
        informationCommissionersOfficeNumber: formValues.firm.informationCommissionersOfficeNumber,
        hasHPPPermissions: hasH,
        authorisationType: formValues.firm.authorisationType as AuthorisationType,
        website: formValues.firm.website,
        address: formValues.firm.address,
      });

      const firmId = res?.firmId;
      if (!firmId) {
        enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.missingFirmId' }), { variant: 'warning' });
        return;
      }

      if (formValues.firm.authorisationType === AuthorisationType.AUTHORISED_REPRESENTATIVE) {
        const networkId =
          formValues.network.backendId ??
          (
            await networkApi.createNetwork({
              name: formValues.network.name,
              fcaNumber: formValues.network.financialConductAuthorityNumber,
              isStrideUpOnPanel: formValues.network.isStrideUpOnPanel ?? null,
              address: toCreateNetworkAddress(formValues.network.address),
            })
          ).id;
        await firmApi.linkFirmToNetwork(firmId, networkId);
        if (!formValues.network.backendId && formValues.network.isPartOfMortgageClub === true) {
          const clubId =
            formValues.mortgageClub.backendId ??
            (
              await mortgageClubApi.createMortgageClub({
                name: formValues.mortgageClub.name,
                fcaNumber: formValues.mortgageClub.financialConductAuthorityNumber,
                address: toCreateClubAddress(formValues.mortgageClub.address),
              })
            ).id;
          await networkApi.linkNetworkToMortgageClub(networkId, clubId);
        }
      } else if (formValues.firm.isPartOfClub === true) {
        const clubId =
          formValues.mortgageClub.backendId ??
          (
            await mortgageClubApi.createMortgageClub({
              name: formValues.mortgageClub.name,
              fcaNumber: formValues.mortgageClub.financialConductAuthorityNumber,
              address: toCreateClubAddress(formValues.mortgageClub.address),
            })
          ).id;
        await firmApi.linkFirmToMortgageClub(firmId, clubId);
      }

      await queryClient.invalidateQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY] });
      await queryClient.refetchQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY], type: 'active' });

      enqueueSnackbar(intl.formatMessage({ id: 'auth.signup.completedCompanyDetails' }), { variant: 'info' });
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(err, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ClerkWrapper width='3xl' onBack={navigateBack}>
      <FormProvider {...form}>
        <h2 className='text-md mb-4 w-full text-center font-semibold'>
          <FormattedMessage id='auth.signup.createOrganisation.title' />
        </h2>
        <form
          onSubmit={e => {
            e.preventDefault();
            const values = getValues();
            if (values.firm.backendId) {
              void onSubmit(values);
              return;
            }
            void handleSubmit(onSubmit)(e);
          }}
          className='w-full max-w-4xl px-2 sm:px-4'
        >
          <FirmForm isFcaLoading={firmFcaLoading} onFcaFind={onFirmFca} showFieldsAfterFca={firmFieldsAfterFca} />
          {showNetwork && (
            <NetworkForm
              isFcaLoading={networkFcaLoading}
              onFcaFind={onNetworkFca}
              disabled={false}
              showFieldsAfterFca={networkFieldsAfterFca}
            />
          )}
          {showClub && (
            <ClubForm
              isFcaLoading={clubFcaLoading}
              onFcaFind={onClubFca}
              disabled={false}
              showFieldsAfterFca={clubFieldsAfterFca}
            />
          )}

          {showConfirmButton && (
            <div className='mt-6 flex w-full max-w-4xl justify-end'>
              <LoadingButton
                isLoading={isSubmitting}
                buttonProps={{ type: 'submit', variant: 'contained', className: 'h-9 min-w-32' }}
              >
                <FormattedMessage id='cta.confirm' />
              </LoadingButton>
            </div>
          )}
        </form>
      </FormProvider>
    </ClerkWrapper>
  );
}
